import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { CommonModule, Location } from '@angular/common';
import { ReportService } from '../../services/report.service';
import { ReportPdfType } from '../../models/v2models/report-pdf-type';
import { ReportV2Service } from '../../services/report-v2.service';
import { SnackbarService } from '../../services/snackbar.service';
@Component({
  selector: 'app-document-preview-component',
  imports: [PdfJsViewerModule, CommonModule],
  templateUrl: './document-preview-component.html',
  styleUrl: './document-preview-component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [],
})
export class DocumentPreviewComponent implements OnInit {
  reportName: string = '';
  jobId: string | null = null;
  reportId: number | null = null;
  blobUrl: string = '';
  aecomReport: string = '';
  public aecomReportURL: string = '';
  fileId: string = '';
  downloadOverrideUrl: string | null = null;
  relativePath: string = '';
  private downloadedBlob!: Blob;
  isDownloading = false;
  hideDownload = false;
  private useDirectPreviewUrl = false;
  private isBlobObjectUrl = false;
  private previewType: ReportPdfType = ReportPdfType.Preview;
  private returnUrl: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private reportService: ReportService,
    private reportV2Service: ReportV2Service,
    private snackbar: SnackbarService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}
  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.reportName = params['reportname'];
      this.aecomReportURL = params['documentUrl'] ? decodeURIComponent(params['documentUrl']) : '';
      this.reportId = Number(params['reportId']) || null;
      this.previewType = this.parseReportPdfType(params['type']);
      this.downloadOverrideUrl = params['downloadOverrideUrl']
        ? decodeURIComponent(params['downloadOverrideUrl'])
        : null;
      this.hideDownload = String(params['hideDownload']).toLowerCase() === 'true';
      this.useDirectPreviewUrl = String(params['directPreview']).toLowerCase() === 'true';
      this.returnUrl = history.state?.returnUrl || null;

      this.fileId = params['fileId'] ? String(params['fileId']) : '';

      if (this.useDirectPreviewUrl && this.reportId) {
        this.loadDirectPreview();
        return;
      }

      this.previewDoc(this.fileId);
    });
  }

  private async loadDirectPreview(): Promise<void> {
    if (!this.reportId) {
      this.aecomReport = '';
      return;
    }

    this.reportV2Service.getReportPdfStream(this.reportId, this.previewType).subscribe({
      next: (response) => {
        const pdfBlob = response.body as Blob;
        this.downloadedBlob = pdfBlob;
        this.aecomReport = URL.createObjectURL(
          new Blob([pdfBlob], { type: 'application/pdf' }),
        );
        this.isBlobObjectUrl = true;
      },
      error: (err: unknown) => {
        void this.handleDirectPreviewError(err);
        this.aecomReport = '';
        this.isBlobObjectUrl = false;
      },
    });
  }

  private async handleDirectPreviewError(err: unknown): Promise<void> {
    const message = await this.extractApiErrorMessage(err);

    this.snackbar.showCustom({
      type: 'error',
      title: 'Error',
      message,
    });

  }

  private async extractApiErrorMessage(err: unknown): Promise<string> {
    const fallback = 'Unable to load PDF preview.';

    if (!(err instanceof HttpErrorResponse)) {
      return fallback;
    }

    const payload = err.error;
    if (typeof payload === 'string') {
      try {
        const parsed = JSON.parse(payload) as { message?: string };
        return parsed.message || payload || fallback;
      } catch {
        return payload || fallback;
      }
    }

    if (payload && typeof payload === 'object' && 'message' in payload) {
      const message = (payload as { message?: unknown }).message;
      return typeof message === 'string' && message.trim() ? message : fallback;
    }

    if (payload instanceof Blob) {
      try {
        const text = await payload.text();
        if (!text.trim()) {
          return fallback;
        }

        try {
          const parsed = JSON.parse(text) as { message?: string };
          return parsed.message || text;
        } catch {
          return text;
        }
      } catch {
        return fallback;
      }
    }

    return fallback;
  }

  private parseReportPdfType(rawType: unknown): ReportPdfType {
    const parsed = Number(rawType);
    if (parsed === ReportPdfType.PriorReport) {
      return ReportPdfType.PriorReport;
    }
    if (parsed === ReportPdfType.FinalReport) {
      return ReportPdfType.FinalReport;
    }
    return ReportPdfType.Preview;
  }

  downloadDoc() {
    this.isDownloading = true;
    this.cdr.detectChanges();

    if (this.downloadOverrideUrl?.trim()) {
      this.reportService.downloadReport('', this.downloadOverrideUrl).subscribe({
        next: (res) => {
          const blob = res.body as Blob;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = this.reportName + '.docx';
          a.click();
          URL.revokeObjectURL(url);
          this.resetDownloadState();
        },
        error: () => {
          this.isDownloading = false;
          this.cdr.detectChanges();
        },
      });

      return;
    }

    if (!this.downloadedBlob) {
      this.isDownloading = false;
      return;
    }

    const url = URL.createObjectURL(this.downloadedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.reportName;
    a.click();
    URL.revokeObjectURL(url);
    this.resetDownloadState();
  }

  private resetDownloadState() {
    setTimeout(() => {
      this.isDownloading = false;
      this.cdr.detectChanges();
    }, 500);
  }

  previewDoc(fileId: string = '') {
    this.reportService.downloadReport(fileId, this.aecomReportURL).subscribe({
      next: (res) => {
        this.downloadedBlob = res.body as Blob;
        const pdfBlob = new Blob([this.downloadedBlob], { type: 'application/pdf' });
        this.aecomReport = URL.createObjectURL(pdfBlob);
        this.isBlobObjectUrl = true;
      },
      error: () => {
        this.aecomReport = '';
        this.isBlobObjectUrl = false;
      },
    });
  }

  closePreview() {
    if (this.aecomReport && this.isBlobObjectUrl) {
      URL.revokeObjectURL(this.aecomReport);
      this.aecomReport = '';
      this.isBlobObjectUrl = false;
    }

    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    this.router.navigate(['/reports']);
  }
}
